import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Paper } from '@mui/material';
import { HotTable } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import Handsontable from 'handsontable';
import { useParams, useSearchParams } from 'react-router';
import dayjs from 'dayjs';

import {
  mapErrorMessageToColumnIndex,
  validateRow,
  validateDropdownValue,
  formatNumberValue,
} from '../../hooks/use-excel-table';
import { useTableHeight } from '../../hooks/use-table-height';
import { formatDateToYYMMDD } from '../materials/datepickers/datepicker-in-excel';
import { useClickOutComponent } from '../../hooks/use-common-actions';

import {
  getHandsontableColumns,
  getHandsontableNestedHeaders,
  MaxLengthEditor,
} from './handsontable.config';
import {
  handleMerchandiseColumn,
  createContextMenuSettings,
  ONE_LINE_FORMULATION_PRODUCT_TYPE_COL,
  tryOpenMerchandisePopupOnEnter,
} from './handsontable-helpers';

import type {
  CellChange,
  CellsFunction,
  ChangeSource,
  ExtendedContextMenuSettings,
  HandsontableColumnSettings,
  HotTableRef,
  PlanTableData,
  RowsWithErrors,
} from './handsontable.types';

import { type SearchOption } from '@/types/search';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import '../../styles/handsontable.styles.css';
import { env } from '@/config/env';
import {
  FORMAT_FULL_YEAR,
  PLAN_FIELDS,
  SELECTED_PLAN_ID_PARAM,
  TYPE_BOOLEAN,
} from '@/lib/constants';

registerAllModules();

Handsontable.editors.registerEditor('maxLengthEditor', MaxLengthEditor);
export interface HandsontableCommonProps {
  tableData?: PlanTableData; // Table data from parent (raw format)
  onDataChange?: (data: PlanTableData) => void; // Callback when table data changes
  plantOptions?: SearchOption[];
  rowsWithErrors?: RowsWithErrors;
  witnessCompanyOptions?: SearchOption[];
  onGetTableData?: (getData: () => PlanTableData) => void;
  onAddRowAbove?: (index: number) => void; // Callback for add row above
  onAddRowBelow?: (index: number) => void; // Callback for add row below
  onDeleteRows?: (indices: number[]) => void; // Callback for batch delete rows
  setRowsWithErrors?: (rowsWithErrors: RowsWithErrors) => void;
  className?: string; // Optional className for custom styling
  readOnly?: boolean; // If true, all cells will be read-only
  cells?: CellsFunction; // Optional cells function for per-cell styling
  showHeaders?: boolean; // If false, hide column headers and nested headers (default: true)
}

export const HandsontableCommon: React.FC<HandsontableCommonProps> = ({
  plantOptions: propsPlantOptions = [],
  rowsWithErrors,
  setRowsWithErrors,
  witnessCompanyOptions: propsWitnessCompanyOptions = [],
  tableData: propsTableData,
  onDataChange,
  onGetTableData,
  onAddRowAbove,
  onAddRowBelow,
  onDeleteRows,
  className,
  readOnly = false,
  cells,
  showHeaders = true,
}) => {
  const { planId } = useParams<{ planId: string }>();
  const [searchParams] = useSearchParams();
  const selectedPlanId = searchParams.get(SELECTED_PLAN_ID_PARAM);

  // Get list of license keys from env
  const licenseKeys = env.HANDSONTABLE_LICENSE_KEY ?? 'non-commercial-and-evaluation';

  const hotTableRef = useRef<Handsontable | null>(null);
  const isUpdatingRef = useRef<boolean>(false);
  const previousRowCountFromPropsRef = useRef<number>(0);

  // Determine if this is a confirm view table (should have auto height)
  // Also used to disable context menu for confirm tables (no need for add/delete row actions)
  const isConfirmView = className?.includes('confirm');

  // Use props options or empty array as fallback
  const plantOptions = propsPlantOptions;
  const witnessCompanyOptions = propsWitnessCompanyOptions;
  const cellErrors = useMemo(() => {
    if (!rowsWithErrors || rowsWithErrors.length === 0) {
      return [];
    }
    return rowsWithErrors.flatMap((row) => {
      return row.field.map((field) => {
        return {
          row: row.rowIndex,
          col: field.index,
          comment: { value: field.message },
        };
      });
    });
  }, [rowsWithErrors]);

  // Use tableData from props only - hook is single source of truth
  // No internal state needed - all data comes from parent (hook)
  const tableData = propsTableData ?? [];

  // Load data from props (from hook) - hook is single source of truth
  // Component only renders what it receives, no conversion logic here
  // IMPORTANT: Only reload when row count changes to preserve user input
  useEffect(() => {
    const currentRowCount = propsTableData?.length || 0;
    const previousRowCount = previousRowCountFromPropsRef.current;

    // Only reload if row count changed (add/delete row) or initial load
    if (propsTableData && propsTableData.length > 0) {
      if (previousRowCount === 0 || currentRowCount !== previousRowCount) {
        const hot = hotTableRef.current;
        if (hot) {
          hot.loadData(propsTableData);
          previousRowCountFromPropsRef.current = currentRowCount;
        }
      }
    }
  }, [propsTableData?.length]); // Only depend on length, not full array

  // Re-apply validation errors when cellErrors change
  // This ensures errors are displayed after add/delete row operations
  useEffect(() => {
    const hot = hotTableRef.current;
    if (!hot) return;

    // Clear all existing comments first
    const totalRows = hot.countRows();
    const totalCols = hot.countCols();
    for (let row = 0; row < totalRows; row++) {
      for (let col = 0; col < totalCols; col++) {
        const cellMeta = hot.getCellMeta(row, col);
        if (cellMeta.comment) {
          hot.setCellMetaObject(row, col, { comment: undefined });
        }
      }
    }

    // Set new validation errors
    if (cellErrors && cellErrors.length > 0) {
      cellErrors.forEach((error) => {
        hot.setCellMetaObject(error.row, error.col, {
          comment: error.comment,
        });
      });
    }

    // Force re-render to display validation errors
    setTimeout(() => {
      const safeHot = hotTableRef.current;
      if (safeHot) {
        safeHot.render();
      }
    }, 0);
  }, [cellErrors]);

  // Removed store sync logic - hook handles all data conversion and sync
  // Component only reacts to propsTableData changes from parent

  // Note: Autocomplete columns now use custom editor (AutocompleteEditor) instead of type 'autocomplete'
  // Options are passed directly to the editor, so no need to update columns when options change
  // The editor will receive updated options automatically when it's opened

  // Sync STT row heights with main table rows
  const syncSttRowHeights = useCallback(() => {
    const hot = hotTableRef.current;
    if (!hot) return;

    const root = hot.rootElement;
    if (!root) return;

    const masterTbody = root.querySelector(
      '.ht_master table.htCore tbody'
    ) as HTMLTableSectionElement | null;
    const leftTbody = root.querySelector(
      '.ht_clone_left table.htCore tbody'
    ) as HTMLTableSectionElement | null;

    if (!masterTbody || !leftTbody) return;

    const masterRows = masterTbody.querySelectorAll('tr');
    const leftRows = leftTbody.querySelectorAll('tr');

    const len = Math.min(masterRows.length, leftRows.length);
    for (let i = 0; i < len; i++) {
      const h = masterRows[i].getBoundingClientRect().height;
      if (h > 0) {
        (leftRows[i] as HTMLTableRowElement).style.height = `${h}px`;
      }
    }
  }, []);

  // Handle before change - validate and ensure autocomplete values are set correctly
  // Note: For dropdown columns with canEnterAnotherValue=false, Handsontable's built-in
  // validation (strict: true, allowInvalid: false) already handles most cases.
  // This handler serves as a safety net for edge cases like:
  // - Paste operations that might bypass Handsontable validation
  // - Programmatic changes via API
  // - Race conditions during rapid input
  const handleBeforeChange = useCallback(
    (changes: (CellChange | null)[] | null, source?: ChangeSource) => {
      if (!changes || changes.length === 0 || source === 'loadData') return;

      // Prevent all changes if readOnly
      if (readOnly) {
        return false; // Reject all changes
      }

      const hot = hotTableRef.current;
      if (!hot) return;

      // Half-width normalization (convertToHalf) for time and quantity columns on every source
      // (paste, autofill, edit). Runs before columnSettings — see formatNumberValue / PLAN_FIELDS.
      for (const change of changes) {
        if (!change) continue;
        const prop = change[1];
        const dataIndex = typeof prop === 'number' && Number.isFinite(prop) ? prop : Number(prop);
        if (!Number.isNaN(dataIndex)) {
          formatNumberValue(change, dataIndex, change[3]);
        }
      }

      // Get column settings
      const columnSettings = hot.getSettings().columns;
      if (!Array.isArray(columnSettings)) return;

      // Helper function to format autocomplete value
      const formatAutocompleteValue = (
        change: CellChange,
        col: number,
        newValue: unknown
      ): void => {
        const isAutocompleteColumn = col === 1 || col === 18;
        if (!isAutocompleteColumn || newValue === null || newValue === undefined) return;

        const stringValue = String(newValue);
        change[3] = stringValue; // Update the newValue in the change array
      };

      // Helper function to format datepicker value
      const formatDatepickerValue = (change: CellChange, col: number, newValue: unknown): void => {
        if (col !== 2 || newValue === null || newValue === undefined) return;

        const valueStr = String(newValue).trim();
        const yymmddRegex = /^([0-9]{2})\/([0-9]{2})\/([0-9]{2})$/;

        // Check if value is already in YY/MM/DD format
        if (yymmddRegex.test(valueStr)) {
          change[3] = valueStr; // Already in YY/MM/DD format, keep as is
        } else {
          // Try to format from YYYY-MM-DD or other formats
          const formattedDate = formatDateToYYMMDD(valueStr);
          change[3] = formattedDate; // Update the newValue to formatted date
        }
      };

      // Process changes for autocomplete and dropdown columns
      for (const change of changes) {
        if (!change) continue;
        const [row, col, , newValue] = change; // change format: [row, col, oldValue, newValue]
        if (typeof col !== 'number') continue;
        const columnConfig = columnSettings[col];

        // remove error highlight from column
        hot.setCellMetaObject(row, col, { comment: undefined });

        if (!columnConfig) continue;

        // Validate dropdown/autocomplete value
        validateDropdownValue(change, col, newValue);

        // Format autocomplete value
        formatAutocompleteValue(change, col, newValue);

        // Format datepicker value
        formatDatepickerValue(change, col, newValue);
      }
    },
    [readOnly]
  );

  // Handle cell changes
  const handleAfterChange = useCallback(
    (changes: (CellChange | null)[] | null, source?: ChangeSource) => {
      if (!changes || changes.length === 0 || source === 'loadData') return;

      const hot = hotTableRef.current;
      if (!hot) return;

      // Get current data from table
      const newTableData: PlanTableData = hot.getData() as PlanTableData;
      const PLAN_ID_COLUMN_INDEX = PLAN_FIELDS.PLAN_ID.id - 1;

      const hasExistingRowId = (rowIndex: number): boolean => {
        const currentRow = newTableData[rowIndex];
        return (
          Array.isArray(currentRow) &&
          currentRow.length > PLAN_ID_COLUMN_INDEX &&
          String(currentRow[PLAN_ID_COLUMN_INDEX] ?? '') !== ''
        );
      };

      // Helper function to validate column 1 (Plant)
      const validatePlantColumn = (newValue: unknown, rowIndex: number): string | null => {
        const isEmpty = !newValue || String(newValue).trim() === '';
        if (isEmpty) {
          return validateRow(newValue, 1); // Get required field message
        }

        if (hasExistingRowId(rowIndex)) {
          // Existing records can keep plant values that are no longer in latest option list.
          return null;
        }

        // Check if value exists in plantOptions
        const valueStr = String(newValue).trim();
        const isValidOption = plantOptions.some(
          (opt) =>
            String(opt.label ?? '')
              .trim()
              .toLowerCase() === valueStr.toLowerCase()
        );
        if (!isValidOption) {
          return 'プラントは必須入力項目です。\nプラント名の入力はサジェストから選択ください。';
        }
        return null;
      };

      // Helper function to validate column 18 (Witness Company)
      const validateWitnessCompanyColumn = (newValue: unknown, rowIndex: number): string | null => {
        const isEmpty = !newValue || String(newValue).trim() === '';
        if (isEmpty) {
          return null; // Allow empty for column 18
        }

        if (hasExistingRowId(rowIndex)) {
          // Existing records can keep witness values that are no longer in latest option list.
          return null;
        }

        // Check if value exists in witnessCompanyOptions
        const valueStr = String(newValue).trim();
        const isValidOption = witnessCompanyOptions.some(
          (opt) => opt.label === valueStr || String(opt.label).trim() === valueStr
        );
        if (!isValidOption) {
          return '提案の中から証人会社を選択してください。';
        }
        return null;
      };

      // Helper function to get error message for a change
      const getErrorMessage = (row: number, col: number, newValue: unknown): string | null => {
        if (col === 1) {
          return validatePlantColumn(newValue, row);
        }
        if (col === 18) {
          return validateWitnessCompanyColumn(newValue, row);
        }
        return validateRow(newValue, col);
      };

      // For autocomplete columns, ensure values are preserved and validate against options
      let newRowsWithErrors = rowsWithErrors ?? [];
      for (const change of changes) {
        if (!change) continue;
        const [row, col, , newValue] = change;
        if (typeof col !== 'number') continue;

        // Validate autocomplete columns: check if value is in options list
        const errorMessage = getErrorMessage(row, col, newValue);

        newRowsWithErrors = mapErrorMessageToColumnIndex(
          newRowsWithErrors ?? [],
          col,
          row,
          errorMessage ?? ''
        );
        if (col === 1 || col === 18) {
          // Update table data with new value (even if empty/null)
          // Handsontable already has the new value in its internal state
          // We just need to sync it to our table data array
          newTableData[row][col] = newValue || '';
        }
      }
      if (setRowsWithErrors) {
        setRowsWithErrors(newRowsWithErrors);
      }
      // Don't convert to CreatePlanRequest here - just keep raw table data
      // Conversion will happen only on submit in use-register-plan.ts
      // Note: Row count tracking is handled separately for props sync

      // Notify parent component of data change
      if (onDataChange) {
        onDataChange(newTableData);
      }

      // Check if any editor is open before rendering
      const activeEditor = hot.getActiveEditor?.();
      const isEditorOpen = activeEditor?.isOpened?.();

      if (!isEditorOpen) {
        // Force render to ensure values are displayed
        setTimeout(() => {
          hot.render();
          window.requestAnimationFrame(() => {
            syncSttRowHeights();
          });
          setTimeout(() => {
            isUpdatingRef.current = false;
          }, 50);
        }, 50);
      } else {
        isUpdatingRef.current = false;
      }
    },
    [
      onDataChange,
      rowsWithErrors,
      setRowsWithErrors,
      plantOptions,
      witnessCompanyOptions,
      syncSttRowHeights,
    ]
  );

  // Handle after selection for autocomplete - ensure value is set
  const handleAfterSelection = useCallback((_row: number, _col: number) => {
    // Placeholder for future use if needed
  }, []);

  // Handle after selection ends - ensure autocomplete value is properly set
  // Note: For autocomplete columns, afterChange already handles value updates correctly.
  // This handler is kept minimal to avoid conflicts and race conditions.
  const handleAfterSelectionEnd = useCallback(
    (row: number, col: number, row2: number, col2: number) => {
      const hot = hotTableRef.current;
      if (!hot) return;

      // For autocomplete columns, afterChange handles everything
      // We don't need to do anything here to avoid race conditions
      // Only handle non-autocomplete columns if needed in the future
      if ((col === 1 || col === 18) && row === row2 && col === col2) {
        // Check if editor is currently active/opened
        const activeEditor = hot.getActiveEditor?.();
        const isEditorOpen = activeEditor?.isOpened?.();

        // If editor is open, don't do anything (it will close the dropdown)
        if (isEditorOpen) {
          return;
        }

        // For autocomplete, Handsontable's built-in editor handles display correctly
        // afterChange already notifies parent with the correct value
        // No need to force update here - it can cause race conditions
      }
    },
    []
  );

  const handleBeforeKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (readOnly) {
        return;
      }
      const hot = hotTableRef.current;
      if (!hot) {
        return;
      }
      const handled = tryOpenMerchandisePopupOnEnter(hot, event, {
        contentCol: 11,
        formulationProductTypeCol: ONE_LINE_FORMULATION_PRODUCT_TYPE_COL,
      });
      if (handled) {
        return false;
      }
    },
    [readOnly]
  );

  // Handle cell click
  const handleBeforeOnCellMouseDown = useCallback(
    (event: MouseEvent, coords: { row: number; col: number }) => {
      const hot = hotTableRef.current;
      if (!hot || coords.row < 0) return;

      // Open merchandise popup on double-click for column 11 (内容) only when 種類 (col 10) is 生コン
      if (coords.col === 11) {
        handleMerchandiseColumn(event, hot, coords, ONE_LINE_FORMULATION_PRODUCT_TYPE_COL);
        return;
      }
      const target = event.target as HTMLElement;
      if (target.classList.contains('dropdown-icon')) {
        event.stopPropagation();
        hot.selectCell(coords.row, coords.col);
        setTimeout(() => {
          const safeHot = hotTableRef.current;
          if (safeHot) {
            safeHot.getActiveEditor()?.beginEditing();
          }
        }, 0);
      }
    },
    []
  );

  // Handle after render - sync STT column heights
  const handleAfterRender = useCallback(() => {
    window.requestAnimationFrame(() => {
      syncSttRowHeights();
    });
  }, [syncSttRowHeights]);

  // Handle after view render - sync STT column heights (more reliable for viewport changes)
  const handleAfterViewRender = useCallback(() => {
    window.requestAnimationFrame(() => {
      syncSttRowHeights();
    });
  }, [syncSttRowHeights]);

  // Handle before context menu - only show for row headers, disable if readOnly or confirm table
  const handleBeforeOnCellContextMenu = useCallback(
    (_event: MouseEvent, coords: { row: number; col: number }) => {
      // If readOnly or confirm table, disable all context menus
      if (readOnly || isConfirmView) {
        return false;
      }
      // Only show context menu when clicking on row headers (col === -1)
      // For other cells/headers, return false to disable Handsontable menu
      // but don't stop propagation or prevent default to allow browser menu
      if (coords.col !== -1) {
        // Stop Handsontable from handling this event, allow browser context menu
        _event.stopImmediatePropagation();
        return false; // Disable Handsontable context menu
      }
      return true; // Show Handsontable context menu for row headers
    },
    [readOnly, isConfirmView]
  );

  // Context menu configuration - only show 3 actions with Japanese labels
  // Use beforeContextMenuShow to double-check and prevent menu on non-row-headers
  // Disable context menu if readOnly or if it's a confirm view table
  // Context menu configuration using common function
  const contextMenuSettings = useMemo(
    () =>
      createContextMenuSettings({
        onAddRowAbove,
        onAddRowBelow,
        onDeleteRows,
        readOnly,
        isConfirmView,
      }),
    [onAddRowAbove, onAddRowBelow, onDeleteRows, readOnly, isConfirmView]
  );

  // Calculate fixed height based on viewport to fill space above footer buttons
  const [tableHeight, setTableHeight] = useState<number>(550);
  const [maxTableHeight, setMaxTableHeight] = useState<number>(550);
  const containerRef = useRef<HTMLDivElement>(null);

  // For confirm view, calculate height based on row count
  // For normal view, use viewport-based calculation
  useEffect(() => {
    if (isConfirmView && tableData.length > 0) {
      // Calculate height based on row count
      // Header: ~60px (2 header rows), Row height: ~35px, Padding: ~20px
      const headerHeight = 60;
      const rowHeight = 35;
      const padding = 20;
      const calculatedHeight = headerHeight + tableData.length * rowHeight + padding;
      setTableHeight(Math.max(200, calculatedHeight)); // Minimum 200px
    }
  }, [isConfirmView, tableData.length]);

  // Use viewport-based calculation for max-height (used in confirm view for scroll)
  // and actual height (used in normal view)
  const setTableHeightWrapper = useCallback(
    (height: number | ((prev: number) => number)) => {
      const newHeight = typeof height === 'function' ? height(maxTableHeight) : height;
      if (isConfirmView) {
        // For confirm view, use this as max-height for scroll
        setMaxTableHeight(newHeight);
      } else {
        // For normal view, use this as actual height
        setTableHeight(newHeight);
      }
    },
    [isConfirmView, maxTableHeight]
  );

  useTableHeight(setTableHeightWrapper);

  // Deselect table when clicking outside the component
  useClickOutComponent(containerRef, hotTableRef);

  // Set max-height CSS variable for confirm view tables
  useEffect(() => {
    if (isConfirmView && containerRef.current) {
      containerRef.current.style.setProperty('--max-table-height', `${maxTableHeight}px`);
    }
  }, [isConfirmView, maxTableHeight]);

  // Sync height on scroll and after table is ready
  useEffect(() => {
    const hot = hotTableRef.current;
    if (!hot) return;

    const syncOnScroll = () => {
      window.requestAnimationFrame(() => {
        syncSttRowHeights();
      });
    };

    // Sync on scroll
    const scrollContainer = hot.rootElement.querySelector('.ht_master .wtHolder');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', syncOnScroll, { passive: true });
    }

    // Initial sync after a delay to ensure table is fully rendered
    const initialSyncTimeout = setTimeout(() => {
      syncSttRowHeights();
    }, 200);

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', syncOnScroll);
      }
      clearTimeout(initialSyncTimeout);
    };
  }, [tableData.length, syncSttRowHeights]);

  // Expose method to get table data for parent component
  useEffect(() => {
    if (onGetTableData) {
      onGetTableData(() => {
        const hot = hotTableRef.current;
        if (!hot) return [];
        return hot.getData() as PlanTableData;
      });
    }
  }, [onGetTableData]);

  // Column definitions - use useMemo to update when options change
  const columns = useMemo(() => {
    const baseColumns = getHandsontableColumns(plantOptions, witnessCompanyOptions);
    // If readOnly, disable all editors
    if (readOnly) {
      return baseColumns.map((col) => ({
        ...col,
        readOnly: true,
        editor: false,
      }));
    }
    return baseColumns;
  }, [plantOptions, witnessCompanyOptions, readOnly]);

  // Nested headers configuration according to design
  const nestedHeaders = useMemo(() => getHandsontableNestedHeaders(), []);

  useEffect(() => {
    const hot = hotTableRef.current;
    if (!hot) return;
    setTimeout(() => {
      hot.render();
    }, 100);
  }, [cellErrors]);

  const PLACEMENT_DATE_COLUMN_INDEX = PLAN_FIELDS.PLACEMENT_DATE.id - 1;

  const renderCell = (TD: HTMLTableCellElement, row: number, col?: number) => {
    if (col === PLACEMENT_DATE_COLUMN_INDEX) {
      TD.setAttribute('translate', 'no');
    }

    if (!planId) return;
    const TR = TD.parentElement;
    if (TR) {
      const placementDate = tableData[row][PLAN_FIELDS.PLACEMENT_DATE.id - 1];
      const isArchive = tableData[row][PLAN_FIELDS.IS_ARCHIVE.id - 1];
      const planId = tableData[row][PLAN_FIELDS.PLAN_ID.id - 1];
      const rowPlacementDate = dayjs(dayjs().format(FORMAT_FULL_YEAR)).isAfter(
        '20' + placementDate
      );
      if (rowPlacementDate) {
        TR.classList.add('is-past');
      } else {
        TR.classList.remove('is-past');
      }
      if (isArchive === TYPE_BOOLEAN.TRUE) {
        TR.classList.add('is-archive');
      } else {
        TR.classList.remove('is-archive');
      }
      if (planId === selectedPlanId) {
        TR.classList.add('notice');
      } else {
        TR.classList.remove('notice');
      }
    }
  };
  return (
    <Paper
      elevation={1}
      sx={{ p: 1, minHeight: isConfirmView ? 'auto' : '400px' }}
      className={className ? `handsontable-paper-${className}` : undefined}
    >
      <div
        ref={containerRef}
        className={`handsontable-only-one-line handsontable-container ${className || ''}`}
      >
        <HotTable
          ref={(hotInstance: HotTableRef | null) => {
            if (hotInstance?.hotInstance) {
              hotTableRef.current = hotInstance.hotInstance;
            }
          }}
          data={tableData}
          cell={cellErrors}
          cells={cells}
          afterRenderer={renderCell}
          comments={true}
          columns={columns as HandsontableColumnSettings[]}
          colHeaders={showHeaders}
          rowHeaders={true}
          nestedHeaders={showHeaders ? nestedHeaders : undefined}
          width='100%'
          height={tableHeight}
          licenseKey={licenseKeys}
          beforeChange={handleBeforeChange}
          beforeKeyDown={handleBeforeKeyDown}
          afterChange={handleAfterChange}
          afterSelection={handleAfterSelection}
          afterSelectionEnd={handleAfterSelectionEnd}
          afterRender={handleAfterRender}
          afterViewRender={handleAfterViewRender}
          beforeOnCellMouseDown={handleBeforeOnCellMouseDown}
          beforeOnCellContextMenu={handleBeforeOnCellContextMenu}
          contextMenu={contextMenuSettings as ExtendedContextMenuSettings | false}
          manualColumnResize={true}
          manualRowResize={false}
          stretchH='all'
          autoWrapRow={true}
          autoWrapCol={true}
          fixedRowsTop={0}
          columnSorting={false}
          filters={false}
          dropdownMenu={true}
          allowInsertRow={!readOnly}
          allowInsertColumn={false}
          allowRemoveRow={!readOnly}
          autoColumnSize={true}
          autoRowSize={true}
          allowRemoveColumn={false}
          outsideClickDeselects={false}
          selectionMode='multiple'
          currentRowClassName='current-row-highlight'
          readOnly={readOnly}
        />
      </div>
    </Paper>
  );
};
